import * as React from "react";
import { Container, Table, OverlayTrigger, Row, Col } from "react-bootstrap";
import * as Routes from "lib/routes";
import I18n from "lib/I18n";
import tooltips from "../../lib/tooltips";
import style from "./style.module.css";
import {
  BsBoxArrowUpRight,
  BsDashCircle,
  BsExclamationTriangle,
  BsInfoCircleFill,
  BsPlusCircle,
} from "react-icons/bs";
import { Item } from "../../store/Item";
import { getStore } from "../../store";
import { observer } from "mobx-react-lite";

interface ValueColumnProps {
  keyName: string;
  value?: string;
}

export const ValueColumn = observer((props: ValueColumnProps) => {
  const { config } = getStore();
  const [isFocused, setIsFocused] = React.useState(false);

  const onMouseEnter = () => {
    setIsFocused(true);
  };

  const onMouseLeave = () => {
    setIsFocused(false);
  };

  const renderToggleHexIcon = (key: string) => {
    if (isFocused) {
      const [handler, icon, messageKey] = config.hexDataKeys.includes(key)
        ? [
            (key: string) => config.removeHexDataKey(key),
            // eslint-disable-next-line react/jsx-key
            <BsDashCircle />,
            "remove_hex_data_key",
          ]
        : [
            (key: string) => config.hexDataKeys.push(key),
            // eslint-disable-next-line react/jsx-key
            <BsPlusCircle />,
            "add_hex_data_key",
          ];

      const onClick = () => handler(key);
      return (
        <OverlayTrigger
          delay={500}
          placement="top"
          overlay={tooltips[messageKey]}
        >
          <span
            onClick={onClick}
            className={`${messageKey} ${style.toggleIcon}`}
          >
            {icon}
          </span>
        </OverlayTrigger>
      );
    }
    return (
      <span style={{ opacity: 0 }}>
        <BsPlusCircle />
      </span>
    );
  };

  const { keyName, value } = props;
  const isPrimitive = value == null || !value.includes("\n");
  const icons = isPrimitive ? renderToggleHexIcon(keyName) : undefined;
  const jsxValue =
    value != null && value.includes("\n") ? <pre>{value}</pre> : value;
  return (
    <td
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{ minWidth: 180 }}
    >
      {jsxValue}
      {icons}
    </td>
  );
});

interface DataEntryListProps {
  item: Item;
}

export const DataEntryList = observer((props: DataEntryListProps) => {
  const { app, detail, config } = getStore();
  const { item } = props;

  const renderEventMasterNotDefined = () => {
    if (item && item.jsonWarnings["event_master_not_defined"]) {
      return (
        <p className="alert alert-warning">
          <span className="text-warning">
            <BsExclamationTriangle />
          </span>
          &nbsp;
          {I18n.t("raw_post_viewer.messages.event_master_not_defined")}
        </p>
      );
    }
    return null;
  };

  const renderType = (type: string) => {
    if (type) {
      return <span>{type}</span>;
    }
    return (
      <span className="text-warning">
        <BsExclamationTriangle />
        &nbsp;
        {I18n.t("raw_post_viewer.label.undefined_key")}
      </span>
    );
  };

  const renderErrors = (errors: string[]) => {
    if (errors.length > 0) {
      return (
        <span className="text-danger">
          <BsExclamationTriangle />
          &nbsp;
          {errors.map((error, i) => (
            <div key={i}>{error}</div>
          ))}
        </span>
      );
    }

    return null;
  };

  const renderSystemKeys = () => {
    const displayIdAsHex = config.displayIdAsHex;
    const systemEntries = Object.entries(
      item.systemEntries(displayIdAsHex),
    ).map(([k, v]) => (
      <Row key={k}>
        <Col xs={3}>{k}</Col>
        <Col xs={6}>{v}</Col>
      </Row>
    ));

    return <Container>{systemEntries}</Container>;
  };

  const renderLinkToEventMaster = () => {
    if (item.eventMasterId) {
      const path = Routes.define_event_master_path(item.eventMasterId);
      const label = I18n.t("raw_post_viewer.label.link_to_event_master", {
        event_id: item.eventId,
      });
      return (
        <div className="alert alert-info">
          {label}
          &nbsp;
          <a
            className="btn btn-sm btn-info"
            href={path}
            target="_blank"
            rel="noreferrer"
          >
            <BsBoxArrowUpRight />
          </a>
        </div>
      );
    }
    return null;
  };

  const dataEntries = item.dataEntries(new Set(config.hexDataKeys));
  const jsonDataKeys = Object.keys(dataEntries);
  const jsonErrors = item.jsonErrors;
  const definition = item.eventMasterDefinition;
  let definitionKeys = Object.keys(item.eventMasterDefinition);
  const nestedJsonDataFirstKeys = new Set(
    jsonDataKeys
      .filter((key) => !!key.match(/[[.]/))
      .map((key) => {
        const index = key.search(/[[.]/);
        return key.substring(0, index);
      }),
  );
  definitionKeys = definitionKeys.filter(
    (key) => !nestedJsonDataFirstKeys.has(key),
  );
  const dataKeys = Array.from(
    new Set(jsonDataKeys.concat(definitionKeys)),
  ).sort();
  const entries = dataKeys.map((key) => {
    const value = dataEntries[key];
    const type = definition[key] ?? "";
    const errors = jsonErrors[key] ?? [];
    return (
      <tr key={key} data-raw-post-key={key}>
        <td>{key}</td>
        <ValueColumn keyName={key} value={value} />
        {!app.idScrambled && <td>{renderType(type)}</td>}
        {!app.idScrambled && <td>{renderErrors(errors)}</td>}
      </tr>
    );
  });

  return (
    <div>
      {!app.idScrambled && renderEventMasterNotDefined()}
      {!app.idScrambled && renderLinkToEventMaster()}
      <h3>
        {I18n.t("raw_post_viewer.label.system_entries")}
        <BsInfoCircleFill
          className={style.pointer}
          onClick={() => (detail.tab = "help")}
        />
      </h3>
      {renderSystemKeys()}
      <h3>{I18n.t("raw_post_viewer.label.data_entries")}</h3>
      <Table striped hover size="sm">
        <thead>
          <tr>
            <th> {I18n.t("raw_post_viewer.label.key")} </th>
            <th> {I18n.t("raw_post_viewer.label.value")} </th>
            {!app.idScrambled && (
              <th> {I18n.t("raw_post_viewer.label.defined_type")} </th>
            )}
            {!app.idScrambled && <th />}
          </tr>
        </thead>
        <tbody>{entries}</tbody>
      </Table>
      {!app.idScrambled && (
        <div className="alert alert-warning">
          {I18n.t("raw_post_viewer.messages.excuse")}
        </div>
      )}
    </div>
  );
});
